import { MapPin, Phone, Mail, Clock } from "lucide-react";
import Reveal, { Eyebrow } from "../components/Reveal";
import ContactForm from "../components/ContactForm";
import { CONTACT } from "../data/content";
import { useLang } from "../i18n/LanguageContext";

export default function Contact() {
  const { t } = useLang();
  const labels = t.contact.labels;

  const CARDS = [
    {
      icon: MapPin,
      label: labels.address,
      value: CONTACT.address,
      href: "https://www.google.com/maps/search/?api=1&query=234+Cite+Golf+Camberene+Dakar",
      testid: "contact-address",
      external: true,
    },
    {
      icon: Phone,
      label: labels.phone,
      value: CONTACT.phone,
      href: `tel:${CONTACT.phoneHref}`,
      testid: "contact-phone",
    },
    {
      icon: Mail,
      label: labels.email,
      value: CONTACT.email,
      href: `mailto:${CONTACT.email}`,
      testid: "contact-email",
    },
  ];

  return (
    <div data-testid="contact-page">
      {/* Header */}
      <section className="navy-texture text-white" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <Reveal>
            <Eyebrow dark>{t.cta.eyebrow}</Eyebrow>
            <h1 className="mt-6 font-serif-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] max-w-3xl text-balance">
              {t.contact.heroTitle}
            </h1>
            <p className="mt-6 text-lg text-slate-200 max-w-2xl leading-relaxed">
              {t.contact.heroText}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Contact cards + map */}
      <section className="bg-white" data-testid="contact-details">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="grid gap-12 lg:grid-cols-12 lg:gap-16 items-start">
            <div className="lg:col-span-7">
              <ContactForm />
            </div>

            <div className="lg:col-span-5">
              <Reveal>
                <Eyebrow>{t.contact.coordsEyebrow}</Eyebrow>
                <h2 className="mt-6 font-serif-display text-3xl sm:text-4xl font-bold tracking-tight text-[#1B3A5B] text-balance">
                  {t.contact.coordsTitle}
                </h2>
              </Reveal>

              <div className="mt-10 space-y-5">
                {CARDS.map((c, i) => {
                  const Icon = c.icon;
                  return (
                    <Reveal key={c.label} delay={i}>
                      <a
                        href={c.href}
                        data-testid={c.testid}
                        target={c.external ? "_blank" : undefined}
                        rel={c.external ? "noopener noreferrer" : undefined}
                        className="group flex items-start gap-4 bg-[#F8FAFC] border border-slate-200 rounded-sm p-6 transition-all duration-300 hover:shadow-lg hover:border-[#C9A15A]"
                      >
                        <span className="h-12 w-12 rounded-full bg-[#1B3A5B] flex items-center justify-center shrink-0 transition-transform duration-300 group-hover:scale-105">
                          <Icon className="h-5 w-5 text-[#C9A15A]" />
                        </span>
                        <span>
                          <span className="block text-xs uppercase tracking-widest text-slate-500">
                            {c.label}
                          </span>
                          <span className="mt-1 block font-medium text-[#1B3A5B] group-hover:text-[#C9A15A] transition-colors break-words">
                            {c.value}
                          </span>
                        </span>
                      </a>
                    </Reveal>
                  );
                })}

                <Reveal delay={3}>
                  <div className="flex items-start gap-4 p-6">
                    <span className="h-12 w-12 rounded-full bg-[#C9A15A]/12 flex items-center justify-center shrink-0">
                      <Clock className="h-5 w-5 text-[#C9A15A]" />
                    </span>
                    <span>
                      <span className="block text-xs uppercase tracking-widest text-slate-500">
                        {labels.availability}
                      </span>
                      <span className="mt-1 block font-medium text-[#1B3A5B]">
                        {t.contact.availabilityValue}
                      </span>
                    </span>
                  </div>
                </Reveal>
              </div>
            </div>
          </div>

          <Reveal delay={1}>
            <div className="mt-16 overflow-hidden rounded-sm border border-slate-200 shadow-sm">
              <iframe
                title="Localisation DS EXPERT — Cité Golf, Cambérène, Dakar"
                data-testid="contact-map"
                src="https://www.google.com/maps?q=Cite%20Golf%20Camberene%20Dakar%20Senegal&output=embed"
                className="w-full h-[400px] md:h-[480px]"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
